import {
  TextField,
  Box,
  TextFieldProps,
} from '@mui/material';

interface SingleDateFieldProps {
  mode: 'single';
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
}

interface RangeDateFieldProps {
  mode: 'range';
  startValue: string;
  endValue: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  startLabel?: string;
  endLabel?: string;
  startError?: boolean;
  endError?: boolean;
  startHelperText?: string;
  endHelperText?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  autoAdjustDates?: boolean;
}

type DateFieldsProps = SingleDateFieldProps | RangeDateFieldProps;

export const DateFields: React.FC<DateFieldsProps> = (props) => {
  const commonTextFieldProps: Partial<TextFieldProps> = {
    type: 'date',
    InputLabelProps: { shrink: true },
    fullWidth: props.fullWidth !== false,
    required: props.required !== false,
    disabled: props.disabled,
  };

  if (props.mode === 'single') {
    return (
      <Box sx={{ mt: 3 }}>
        <TextField
          {...commonTextFieldProps}
          label={props.label || 'Date'}
          value={props.value || ''}
          onChange={(e) => props.onChange(e.target.value)}
          error={props.error}
          helperText={props.helperText}
        />
      </Box>
    );
  }

  // Range mode
  const handleStartDateChange = (value: string) => {
    props.onStartChange(value);

    // Auto-adjust dates if enabled
    if (props.autoAdjustDates !== false) {
      // If no end date is set, default it to the start date (single-day)
      if (value && !props.endValue) {
        props.onEndChange(value);
      }
      // If end date is before new start date, adjust it
      else if (value && props.endValue && props.endValue < value) {
        props.onEndChange(value);
      }
    }
  };

  const handleEndDateChange = (value: string) => {
    props.onEndChange(value);

    // Auto-adjust dates if enabled
    if (props.autoAdjustDates !== false) {
      // If end date is set before start date, auto-adjust start date
      if (value && props.startValue && value < props.startValue) {
        props.onStartChange(value);
      }
    }
  };

  return (
    <Box display="flex" gap={2} sx={{ mt: 3 }}>
      <TextField
        {...commonTextFieldProps}
        label={props.startLabel || 'Start Date'}
        value={props.startValue || ''}
        onChange={(e) => handleStartDateChange(e.target.value)}
        error={props.startError}
        helperText={props.startHelperText || 'Start date'}
      />
      <TextField
        {...commonTextFieldProps}
        label={props.endLabel || 'End Date'}
        value={props.endValue || ''}
        onChange={(e) => handleEndDateChange(e.target.value)}
        error={props.endError}
        helperText={props.endHelperText || 'End date'}
        inputProps={{ min: props.startValue || undefined }}
      />
    </Box>
  );
};
